import { createAsyncThunk } from "@reduxjs/toolkit";
import { client, ApiResponseData, ApiResponse } from "@api";
import {
  GetCustomTokenRequestBody,
  Organization,
  User,
  UserConnections,
  UserSignUpFlow,
} from "./auth.types";
import { RootState } from "../store";

export const getCustomToken = createAsyncThunk<string, string>(
  "auth/getCustomToken",
  async (authCode, { fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const response = await client.post<
        ApiResponseData<{ customToken: string }>,
        ApiResponse<{ customToken: string }>,
        GetCustomTokenRequestBody
      >("/auth-code", { authCode }, { signal });

      return fulfillWithValue(response.data.payload.customToken);
    } catch (e) {
      return rejectWithValue(null);
    }
  }
);

export const getIdentity = createAsyncThunk<{
  user: User;
  org: Organization;
  userSignUpFlow: UserSignUpFlow;
}>("auth/getIdentity", async (_, { fulfillWithValue, rejectWithValue, signal }) => {
  try {
    const response = await client.get<
      ApiResponseData<{
        user: User;
        org: Organization;
        state: UserSignUpFlow;
      }>
    >(`/api/v1/identity`, {
      signal,
    });

    const { user, org, state } = response.data.payload;

    return fulfillWithValue({ user, org, userSignUpFlow: state });
  } catch (error) {
    return rejectWithValue(null);
  }
});

export const getUserConnections = createAsyncThunk<UserConnections>(
  "auth/getUserConnections",
  async (_, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const response = await client.get<ApiResponseData<UserConnections>>(
        `/api/v1/auth/orgs/${user?.orgId}/users/${user?.userId}/connections`,
        { signal }
      );

      return fulfillWithValue(response.data.payload);
    } catch (e) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const signOut = createAsyncThunk<null>(
  "auth/signOut",
  async (_, { fulfillWithValue, rejectWithValue, signal }) => {
    try {
      await client.get("/logout", { signal });
      return fulfillWithValue(null);
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const updateUserFlags = createAsyncThunk<null, Array<{ name: string; value: boolean }>>(
  "auth/updateUserFlags",
  async (flags, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await client.patch(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/flags`,
        { flags },
        { signal }
      );

      return fulfillWithValue(null);
    } catch (e) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);
